import { useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  AlertCircle,
  Building2,
  FolderPlus,
  Info,
  Plus,
  Warehouse,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  buildStructureTree,
  isCapable,
  CAPABILITY_SOON_HINT,
} from './structureLogic';
import type { FarmWithAggregate } from './types';
import { useStructureData } from './hooks/useStructureQueries';
import { StructureTree } from './components/StructureTree';
import { FarmDrawer } from './components/FarmDrawer';
import { ClusterDialog } from './components/ClusterDialog';
import { MoveClusterDialog } from './components/MoveClusterDialog';
import { ArchiveFarmDialog } from './components/ArchiveFarmDialog';
import { RestoreFarmDialog } from './components/RestoreFarmDialog';

/**
 * StructurePanel — содержимое экрана «Структура» (AK-345) без внешнего
 * AppShell-обёртки.
 *
 * Вынесено из StructurePage (AK-350), чтобы то же дерево использовалось как
 * вкладка в разделе «Компания» (/company/structure) и как самостоятельная
 * страница /structure (редиректится в /company/structure). Логика без изменений.
 *
 * По умолчанию рендерит собственный PageHeader (заголовок «Структура»);
 * внутри раздела «Компания» шапку даёт CompanyLayout — передаём
 * `showHeader={false}` и получаем только тулбар действий + дерево.
 */
export function StructurePanel({
  showHeader = true,
}: {
  showHeader?: boolean;
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  // include_archived управляется тумблером «Показать архивные» (за capability).
  const { companies, clusters, farms, isLoading, error, companyId } =
    useStructureData(showArchived && isCapable('showArchivedFarms'));

  const [farmDrawerOpen, setFarmDrawerOpen] = useState(false);
  const [clusterDialogOpen, setClusterDialogOpen] = useState(false);
  const [editFarm, setEditFarm] = useState<FarmWithAggregate | null>(null);
  const [moveFarm, setMoveFarm] = useState<FarmWithAggregate | null>(null);
  const [archiveFarm, setArchiveFarm] = useState<FarmWithAggregate | null>(null);
  const [restoreFarm, setRestoreFarm] = useState<FarmWithAggregate | null>(null);

  const farmById = useMemo(
    () => new Map(farms.map((f) => [f.id, f])),
    [farms],
  );

  const tree = useMemo(
    () =>
      buildStructureTree(
        companies.map((c) => ({ id: c.id, name: c.name })),
        clusters.map((c) => ({ id: c.id, name: c.name, company_id: c.company_id })),
        farms,
        search,
      ),
    [companies, clusters, farms, search],
  );

  const isForbidden = (error as { status?: number } | null)?.status === 403;
  const isEmpty = !isLoading && !error && farms.length === 0 && clusters.length === 0;
  // Активные хозяйства — для правила «нельзя архивировать последнее». При
  // include_archived список содержит и архивные, поэтому фильтруем по флагу.
  const activeFarmCount = farms.filter((f) => !f.archived).length;

  // canManage: если бэк вернул данные без 403, пользователь как минимум читает.
  // Действия финально проверит бэк (require_structure_write) — при 403 на
  // мутацию drawer/dialog покажет ошибку. Гейт пункта навигации — в Sidebar.
  const canManage = !isForbidden;

  // На свежем тенанте записи компании ещё нет, а хозяйство и кластер без неё
  // не создать (`FarmDrawer`/`ClusterDialog` требуют `companyId`). Раньше эти
  // диалоги просто не монтировались — кнопки «Создать хозяйство» и «Кластер»
  // молча ничего не делали ровно у того, кому этот экран и адресован.
  // Теперь без компании кнопок нет, а есть ссылка туда, где компания заводится.
  const hasCompany = companyId !== null;

  /**
   * Клик по хозяйству в дереве ведёт в его КАРТОЧКУ (AK-746), а не в drawer
   * реквизитов. До этого «открыть хозяйство» означало «открыть форму его ИНН»,
   * хотя человек шёл смотреть землю: сколько её, что с планом, где архив.
   *
   * Drawer никуда не делся — он остаётся редактором реквизитов, и карточка
   * открывает его кнопкой «Изменить сведения» (и он же открывается по
   * «Переименовать» из меню строки).
   *
   * AK-899: карточка стала разделом «Данные хозяйства» (`/farm/<id>`).
   */
  const openFarmCard = (farmId: string) => {
    void navigate({ to: '/farm/$farmId', params: { farmId } });
  };

  /**
   * AK-584 — «＋ Добавить поле» из дерева/карточки хозяйства. Ведёт в уже
   * работающий режим «+ Поле» на карте, но с предвыбранным хозяйством: человек
   * стоит внутри «Северного», спрашивать «в какое хозяйство?» тут глупо.
   */
  const addFieldToFarm = (farmId: string) => {
    void navigate({ to: '/map', search: { addField: 1, farm: farmId } });
  };

  const openRename = (farmId: string) => {
    // Переименование хозяйства — тот же drawer (имя + всё остальное).
    const farm = farmById.get(farmId);
    if (farm) setEditFarm(farm);
  };

  const actions = canManage && hasCompany ? (
    <div className="flex gap-2">
      <Button variant="outline" onClick={() => setClusterDialogOpen(true)}>
        <FolderPlus className="h-4 w-4" />
        <span className="hidden sm:inline">Кластер</span>
      </Button>
      <Button
        onClick={() => {
          setEditFarm(null);
          setFarmDrawerOpen(true);
        }}
      >
        <Plus className="h-4 w-4" />
        <span className="hidden sm:inline">Хозяйство</span>
      </Button>
    </div>
  ) : undefined;

  return (
    <>
      {showHeader ? (
        <PageHeader
          title="Структура"
          showBack={false}
          description="Компания, кластеры и хозяйства. Поля группируются по хозяйствам."
          actions={actions}
        />
      ) : (
        actions && <div className="flex justify-end">{actions}</div>
      )}

      {/* Чипы «N кластера · M хозяйств · K полей» отсюда убраны: ровно те же
          числа стоят в строке компании самого дерева, в 150px ниже, и ещё
          другими словами («активных хозяйств» vs «хозяйств»). Один и тот же
          факт дважды на одном экране — прямая усталость (design review
          2026-08-18, зона 2 · А8). Источник правды — строка компании. */}

      {isForbidden && (
        <Alert variant="destructive" className="mt-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            У вас нет прав управлять структурой. Обратитесь к администратору компании.
          </AlertDescription>
        </Alert>
      )}

      {error && !isForbidden && (
        <Alert variant="destructive" className="mt-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Не удалось загрузить структуру. Обновите страницу.
          </AlertDescription>
        </Alert>
      )}

      {!isLoading && !error && !isEmpty && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Input
            placeholder="Поиск по кластеру или хозяйству…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 max-w-xs"
          />
          {isCapable('showArchivedFarms') ? (
            <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
              <Checkbox
                checked={showArchived}
                onCheckedChange={(v) => setShowArchived(v === true)}
              />
              Показать архивные
            </label>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <label className="flex cursor-not-allowed items-center gap-2 text-sm text-muted-foreground/50">
                  <Checkbox checked={false} disabled />
                  Показать архивные
                </label>
              </TooltipTrigger>
              <TooltipContent>{CAPABILITY_SOON_HINT}</TooltipContent>
            </Tooltip>
          )}
        </div>
      )}

      {isLoading && (
        <Card className="mt-6 p-4">
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-11 w-full" />
            ))}
          </div>
        </Card>
      )}

      {isEmpty && (
        <Card className="mt-6 px-6 py-12 text-center">
          <div className="mx-auto flex max-w-md flex-col items-center">
            <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-secondary text-muted-foreground">
              <Warehouse className="h-7 w-7" />
            </span>
            <h3 className="text-lg font-semibold text-foreground">
              {hasCompany ? 'Структура пока пустая' : 'Начните с реквизитов компании'}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {hasCompany
                ? 'Добавьте первое хозяйство — потом сгруппируете их по кластерам. Кластеры удобны, когда хозяйств много.'
                : 'Хозяйства и кластеры принадлежат компании, а её реквизиты ещё не заполнены. Заполните их на вкладке «Профиль» — потом вернитесь сюда и добавьте первое хозяйство.'}
            </p>
            {canManage && !hasCompany && (
              <div className="mt-6">
                <Button asChild variant="accent-brand">
                  <Link to="/company/$tab" params={{ tab: 'profile' }}>
                    <Building2 className="h-4 w-4" />
                    Заполнить реквизиты компании
                  </Link>
                </Button>
              </div>
            )}
            {canManage && hasCompany && (
              <div className="mt-6 flex w-full max-w-xs flex-col gap-2">
                <Button
                  variant="accent-brand"
                  onClick={() => {
                    setEditFarm(null);
                    setFarmDrawerOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4" />
                  Создать хозяйство
                </Button>
                <Button variant="outline" onClick={() => setClusterDialogOpen(true)}>
                  <FolderPlus className="h-4 w-4" />
                  Создать кластер
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}

      {!isLoading && !error && !isEmpty && (
        <div className="mt-4">
          <StructureTree
            tree={tree}
            activeFarmCount={activeFarmCount}
            canManage={canManage}
            onRename={openRename}
            onMove={(id) => setMoveFarm(farmById.get(id) ?? null)}
            onOpen={openFarmCard}
            onArchive={(id) => setArchiveFarm(farmById.get(id) ?? null)}
            onRestore={(id) => setRestoreFarm(farmById.get(id) ?? null)}
            onAddField={addFieldToFarm}
          />
          {search && tree.clusters.length === 0 && tree.noCluster.length === 0 && (
            <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
              <Info className="h-4 w-4" />
              Ничего не найдено по «{search}».
            </div>
          )}
        </div>
      )}

      {/* Drawers / dialogs */}
      {companyId && (
        <>
          <FarmDrawer
            open={farmDrawerOpen || editFarm !== null}
            onOpenChange={(o) => {
              if (!o) {
                setFarmDrawerOpen(false);
                setEditFarm(null);
              }
            }}
            companyId={companyId}
            clusters={clusters}
            farm={editFarm}
            onAddField={addFieldToFarm}
          />
          <ClusterDialog
            open={clusterDialogOpen}
            onOpenChange={setClusterDialogOpen}
            companyId={companyId}
          />
        </>
      )}
      <MoveClusterDialog
        open={moveFarm !== null}
        onOpenChange={(o) => !o && setMoveFarm(null)}
        farm={moveFarm}
        clusters={clusters}
      />
      <ArchiveFarmDialog
        open={archiveFarm !== null}
        onOpenChange={(o) => !o && setArchiveFarm(null)}
        farm={archiveFarm}
      />
      <RestoreFarmDialog
        open={restoreFarm !== null}
        onOpenChange={(o) => !o && setRestoreFarm(null)}
        farm={restoreFarm}
      />
    </>
  );
}

export default StructurePanel;
